/**
 * Custom personas storage (PR Q3 · §Q3.3).
 *
 * localStorage-backed CRUD for user-saved persona presets. Built-in
 * presets stay in `state/personaPresets.ts`; this module manages the
 * user-scoped extensions. Future Firestore swap stays mechanical —
 * the API surface (read/save/delete) doesn't change.
 *
 * Pre-prod: no auth context; everything is local-only.
 */
import type { ConstellationFilters } from './filters.js';
import { isQuotaError, type PersistResult } from './qualityPersistence.js';

export interface CustomPersona {
  id: string;
  label: string;
  description: string;
  /** Filter patch merged on top of the current filters. */
  filters: Partial<ConstellationFilters>;
}

const KEY = 'atlas.compliance.customPersonas';

let memoryStore: Record<string, string> = {};

function getStore(): { get: () => string | null; set: (v: string) => void } {
  if (typeof localStorage !== 'undefined') {
    return {
      get: () => localStorage.getItem(KEY),
      set: (v) => localStorage.setItem(KEY, v),
    };
  }
  return {
    get: () => memoryStore[KEY] ?? null,
    set: (v) => { memoryStore[KEY] = v; },
  };
}

function setSerialized(value: CustomPersona[]): PersistResult {
  try {
    getStore().set(JSON.stringify(value));
    return { ok: true };
  } catch (e) {
    if (isQuotaError(e)) {
      return { ok: false, reason: 'quota', message: (e as Error).message };
    }
    return { ok: false, reason: 'unknown', message: e instanceof Error ? e.message : String(e) };
  }
}

function isValidPersona(x: unknown): x is CustomPersona {
  if (typeof x !== 'object' || x === null) return false;
  const p = x as CustomPersona;
  return typeof p.id === 'string' && p.id.length > 0
    && typeof p.label === 'string'
    && typeof p.description === 'string'
    && typeof p.filters === 'object' && p.filters !== null;
}

export function readCustomPersonas(): CustomPersona[] {
  // R1.M1 — shape-validate each item; drop bad entries silently so a
  // poisoned localStorage row can't surface undefined properties to
  // the dialog or break the merge into filters.
  try {
    const raw = getStore().get();
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidPersona);
  } catch {
    return [];
  }
}

export function saveCustomPersona(persona: CustomPersona): PersistResult {
  const all = readCustomPersonas();
  const idx = all.findIndex((p) => p.id === persona.id);
  if (idx >= 0) all[idx] = persona;
  else all.push(persona);
  return setSerialized(all);
}

export function deleteCustomPersona(id: string): PersistResult {
  const all = readCustomPersonas().filter((p) => p.id !== id);
  return setSerialized(all);
}
